'use client';

import React, { useCallback, useEffect, useRef } from 'react';

import { AnimatePresence } from 'framer-motion';

import RichText from '@/components/ui/RichText/RichText';

import { useSpecialitiesFilter } from '../hooks/useSpecialitiesFilter';
import type { Speciality } from '../types';
import SpecialitiesFilterBar from './SpecialitiesFilterBar';
import SpecialityCard from './SpecialityCard';

export default function SpecialitiesGrid({
  initialSpecialities,
  total,
  searchParams: _searchParams,
}: {
  initialSpecialities: Speciality[];
  total: number;
  searchParams: any;
}): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    query,
    setQuery,
    openMenu,
    setOpenMenu,
    isMobile,
    selectedCategory,
    selectedSubSlugs,
    subSpecialityOptions,
    subSpecialityTitleMap,
    subSpecsEnabled,
    filtered,
    visible,
    hasMore,
    isFiltered,
    setVisibleCount,
    handleSearch,
    handleClearAll,
    handleSelectCategory,
    handleToggleSubSlug,
    handleClearSubSlugs,
  } = useSpecialitiesFilter({ initialSpecialities, total });

  const handleOpenMenu = useCallback(
    (key: string | null) => setOpenMenu(key),
    [setOpenMenu],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !isMobile &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile, setOpenMenu]);

  return (
    <section className="content-wrapper relative z-10" id="specialities-search">
      <div className="px-4 py-12">
        <SpecialitiesFilterBar
          query={query}
          onQueryChange={setQuery}
          onSearch={handleSearch}
          openMenu={openMenu}
          onOpenMenu={handleOpenMenu}
          isMobile={isMobile}
          selectedCategory={selectedCategory}
          selectedSubSlugs={selectedSubSlugs}
          subSpecialityOptions={subSpecialityOptions}
          subSpecialityTitleMap={subSpecialityTitleMap}
          subSpecsEnabled={subSpecsEnabled}
          isFiltered={isFiltered}
          onSelectCategory={handleSelectCategory}
          onToggleSubSlug={handleToggleSubSlug}
          onClearSubSlugs={handleClearSubSlugs}
          onClearAll={handleClearAll}
          containerRef={containerRef}
        />

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {visible.map((speciality) => (
              <SpecialityCard key={speciality.id} {...speciality} />
            ))}
          </AnimatePresence>
        </div>

        {hasMore && (
          <div className="mt-10 flex justify-center">
            <button
              onClick={() => setVisibleCount()}
              className="border-info-500-soft text-text-action-teal hover:bg-info-500-soft flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2.5 text-base font-bold transition"
            >
              View More
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="h-4 w-4"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>
        )}

        {!hasMore && filtered.length === 0 && (
          <div className="py-12 text-center">
            <RichText styles={{ p: 'text-lg font-medium text-gray-500' }}>
              {'No specialities match your criteria.'}
            </RichText>
            <button
              onClick={handleClearAll}
              className="text-theme-action-primary mt-4 cursor-pointer text-sm font-semibold hover:underline"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
